import { Injectable } from '@angular/core';
import { parse } from 'exifr';

type ImageSource = string | File | Blob;
type ImageFormat = 'image/jpeg' | 'image/png' | 'image/webp';
type ImageProcessingResult<T extends ImageSource> = T;

export interface SmartResizeOptions {
  /**
   * Area massima in megapixel. Le immagini che la superano vengono ridimensionate
   * proporzionalmente fino a restare entro questo limite.
   * Default: 0.8 MP (~1131×707 per immagini 16:9, ~894×894 per immagini quadrate)
   */
  maxMegapixels?: number;

  /**
   * Lato corto minimo garantito in pixel dopo il ridimensionamento.
   * Evita che immagini molto rettangolari (es. banner, scontrini) perdano leggibilità.
   * Default: 500 px
   */
  minShortSide?: number;

  /**
   * Tetto assoluto del lato lungo in pixel.
   * Impedisce che immagini estremamente allungate sfondino questa soglia
   * anche dopo la correzione del lato corto minimo.
   * Default: 2400 px
   */
  maxLongSide?: number;

  /**
   * Formato di output. Se omesso viene scelto automaticamente:
   * - 'image/png'  → aspect ratio > 3.5 (screenshot, documenti, scontrini)
   * - 'image/webp' → tutto il resto (foto, illustrazioni, contenuti misti)
   */
  format?: ImageFormat;

  /**
   * Qualità di compressione (0–1). Applicata a JPEG e WebP; ignorata da PNG.
   * Default: 0.88
   */
  quality?: number;
}

export interface SmartResizeResult {
  /** Immagine ridimensionata in formato base64 */
  base64: string;
  /** Larghezza finale in pixel */
  width: number;
  /** Altezza finale in pixel */
  height: number;
  /** Formato effettivamente usato */
  format: ImageFormat;
  /** true se l'immagine è stata effettivamente ridimensionata */
  wasResized: boolean;
  /** Rapporto tra lati (lato lungo / lato corto) */
  aspectRatio: number;
}

// ────────────────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class ImageUtilsService {
  private readonly supportedFormats: ImageFormat[] = ['image/jpeg', 'image/png', 'image/webp'];
  private readonly defaultQuality = 0.9;
  private readonly defaultFormat: ImageFormat = 'image/webp';

  // ─── Costanti per smartResizeBase64 ───────────────────────────────────────
  private readonly SMART_DEFAULT_MAX_MP = 0.8;
  private readonly SMART_DEFAULT_MIN_SHORT = 500;
  private readonly SMART_DEFAULT_MAX_LONG = 2400;
  private readonly SMART_DEFAULT_QUALITY = 0.88;

  constructor() {}

  // ══════════════════════════════════════════════════════════════════════════
  // smartResizeBase64
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Ridimensionamento "smart" di un'immagine base64.
   *
   * Applica tre vincoli in cascata per gestire correttamente sia foto quadrate
   * sia immagini molto rettangolari (banner, scontrini, screenshot lunghi):
   *
   *  1. **Budget in megapixel** — scala uniformemente se l'area supera `maxMegapixels`.
   *     Formula: ratio = √(maxPixels / currentPixels), preserva le proporzioni.
   *
   *  2. **Lato corto minimo** — dopo lo scaling, se il lato corto è sceso sotto
   *     `minShortSide`, espande entrambi i lati il minimo necessario.
   *     Questo evita la perdita di leggibilità su immagini molto allungate.
   *
   *  3. **Tetto lato lungo** — se dopo la correzione del lato corto il lato lungo
   *     sfonda `maxLongSide`, ridimensiona di nuovo uniformemente.
   *     Garantisce un limite assoluto indipendente dall'aspect ratio.
   *
   * Il formato viene scelto automaticamente se non specificato:
   * - PNG per immagini con aspect ratio > 3.5 (testo nitido, zero artefatti)
   * - WebP per tutto il resto (miglior rapporto qualità/peso per contenuti misti)
   *
   * @param imageBase64 - Immagine sorgente in formato base64 (con o senza prefisso data:)
   * @param options     - Opzioni di ridimensionamento (tutte opzionali, con defaults sensati)
   * @returns Promise con il risultato: base64, dimensioni finali, formato usato, flag wasResized
   *
   * @example
   * // Utilizzo base — defaults ottimali per documenti con contenuti misti
   * const result = await service.smartResizeBase64(imageBase64);
   * console.log(`${result.width}×${result.height} — ${result.format} — ridimensionata: ${result.wasResized}`);
   *
   * @example
   * // Più stringente per documenti importanti
   * const result = await service.smartResizeBase64(imageBase64, {
   *   maxMegapixels: 0.5,
   *   minShortSide: 400,
   *   quality: 0.85,
   * });
   *
   * @example
   * // Forza PNG lossless (es. screenshot con testo)
   * const result = await service.smartResizeBase64(imageBase64, {
   *   format: 'image/png',
   * });
   *
   * @throws {Error} Se l'immagine non può essere caricata o i parametri non sono validi
   */
  async smartResizeBase64(
    imageBase64: string,
    options: SmartResizeOptions = {},
  ): Promise<SmartResizeResult> {
    const {
      maxMegapixels = this.SMART_DEFAULT_MAX_MP,
      minShortSide = this.SMART_DEFAULT_MIN_SHORT,
      maxLongSide = this.SMART_DEFAULT_MAX_LONG,
      quality = this.SMART_DEFAULT_QUALITY,
    } = options;

    const { width: origW, height: origH } = await this.getImageDimensions(imageBase64);

    // Calcola dimensioni target con i tre vincoli
    const [targetW, targetH] = this.calculateSmartSize(
      origW,
      origH,
      maxMegapixels,
      minShortSide,
      maxLongSide,
    );

    const format = options.format ?? this.chooseSmartFormat(origW, origH);

    // Se le dimensioni coincidono con l'originale, evita un re-encode inutile
    if (targetW === origW && targetH === origH) {
      return {
        base64: imageBase64,
        width: origW,
        height: origH,
        format,
        wasResized: false,
        aspectRatio: this.calcAspectRatio(origW, origH),
      };
    }

    const maxSide = Math.max(targetW, targetH);
    const resizedBase64 = await this.resizeImage(imageBase64, maxSide, format, quality);

    return {
      base64: resizedBase64,
      width: targetW,
      height: targetH,
      format,
      wasResized: true,
      aspectRatio: this.calcAspectRatio(targetW, targetH),
    };
  }

  /**
   * Converte un elemento HTMLImageElement in una stringa base64, con possibilità di ridimensionamento.
   * Gestisce l'orientamento EXIF e ottimizza la gestione delle risorse di memoria.
   *
   * Il formato di default è 'image/webp': offre un peso significativamente inferiore
   * a PNG a parità di qualità visiva. Passa 'image/png' esplicitamente per output lossless.
   *
   * @param imgElement - L'elemento HTML Image da convertire
   * @param maxSize    - Dimensione massima in pixel (opzionale)
   * @param format     - Formato di output (default: 'image/webp')
   * @param quality    - Qualità di compressione tra 0 e 1 (default: 0.9)
   * @returns Promise con la stringa base64 dell'immagine
   *
   * @example
   * const base64 = await service.imageToBase64(imgElement);
   * const base64png = await service.imageToBase64(imgElement, 800, 'image/png');
   *
   * @throws {Error} Se i parametri non sono validi o il processo fallisce
   */
  async imageToBase64(
    imgElement: HTMLImageElement,
    maxSize?: number,
    format: ImageFormat = this.defaultFormat,
    quality: number = this.defaultQuality,
  ): Promise<string> {
    this.validateInputs(maxSize, format, quality);

    let canvas: HTMLCanvasElement | null = null;

    try {
      const orientation = await this.getExifOrientation(imgElement);
      const { naturalWidth, naturalHeight } = imgElement;

      let width = naturalWidth;
      let height = naturalHeight;

      if (maxSize && (width > maxSize || height > maxSize)) {
        [width, height] = this.calculateProportionalSize(width, height, maxSize);
      }

      canvas = this.createOrientedCanvas(width, height, orientation);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      this.applyOrientationTransform(ctx, canvas.width, canvas.height, orientation);
      ctx.drawImage(imgElement, 0, 0, naturalWidth, naturalHeight, 0, 0, width, height);

      return canvas.toDataURL(format, quality);
    } finally {
      this.disposeCanvas(canvas);
    }
  }

  /**
   * Converte un Blob in una stringa base64, restituendo anche le dimensioni originali.
   * Non applica alcun ridimensionamento: usa smartResizeBase64 o resizeImage per quello.
   *
   * @param blob - Il Blob da convertire
   * @returns Promise con base64, width e height originali
   */
  async blobToBase64(blob: Blob): Promise<{ base64: string; width: number; height: number }> {
    let img: HTMLImageElement | null = null;

    try {
      img = await this.loadImage(blob);
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Errore nella conversione del blob in base64'));
        reader.readAsDataURL(blob);
      });

      return { base64, width, height };
    } finally {
      this.disposeImage(img);
    }
  }

  /**
   * Ridimensiona un'immagine mantenendo le proporzioni originali.
   * Gestisce automaticamente l'orientamento EXIF dell'immagine.
   *
   * Il formato di default è 'image/webp'. Passa 'image/png' esplicitamente per lossless.
   *
   * @param image   - Immagine sorgente (base64, File o Blob)
   * @param maxSize - Dimensione massima del lato più lungo in pixel
   * @param format  - Formato di output (default: 'image/webp')
   * @param quality - Qualità di compressione tra 0 e 1 (default: 0.9)
   * @returns Promise con l'immagine ridimensionata nello stesso tipo dell'input
   *
   * @example
   * const resized = await service.resizeImage(base64String, 1024);
   * const resizedFile = await service.resizeImage(file, 800, 'image/jpeg', 0.7);
   *
   * @throws {Error} Se i parametri non sono validi o il processo fallisce
   */
  async resizeImage<T extends ImageSource>(
    image: T,
    maxSize: number,
    format: ImageFormat = this.defaultFormat,
    quality: number = this.defaultQuality,
  ): Promise<ImageProcessingResult<T>> {
    this.validateInputs(maxSize, format, quality);

    return this.processImage(image, format, quality, async (img, orientation) => {
      const { naturalWidth, naturalHeight } = img;
      const swapDimensions = orientation >= 5 && orientation <= 8;
      const originalWidth = swapDimensions ? naturalHeight : naturalWidth;
      const originalHeight = swapDimensions ? naturalWidth : naturalHeight;

      const [newWidth, newHeight] = this.calculateProportionalSize(
        originalWidth,
        originalHeight,
        maxSize,
      );

      const canvas = this.createOrientedCanvas(newWidth, newHeight, orientation);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      this.applyOrientationTransform(ctx, newWidth, newHeight, orientation);
      ctx.drawImage(img, 0, 0, originalWidth, originalHeight, 0, 0, newWidth, newHeight);

      return canvas;
    }) as Promise<ImageProcessingResult<T>>;
  }

  /**
   * Corregge l'orientamento di un'immagine in base ai metadati EXIF.
   * Non modifica le dimensioni, solo l'orientamento.
   *
   * Il formato di default è 'image/webp'. Passa 'image/png' esplicitamente per lossless.
   *
   * @param image   - Immagine sorgente (base64, File o Blob)
   * @param format  - Formato di output (default: 'image/webp')
   * @param quality - Qualità di compressione tra 0 e 1 (default: 0.9)
   * @returns Promise con l'immagine ruotata correttamente nello stesso tipo dell'input
   *
   * @example
   * const corrected = await service.autoRotateImage(imageBlob);
   * const correctedPng = await service.autoRotateImage(file, 'image/png', 1.0);
   *
   * @throws {Error} Se i parametri non sono validi o il processo fallisce
   */
  async autoRotateImage<T extends ImageSource>(
    image: T,
    format: ImageFormat = this.defaultFormat,
    quality: number = this.defaultQuality,
  ): Promise<ImageProcessingResult<T>> {
    this.validateInputs(undefined, format, quality);

    return this.processImage(image, format, quality, async (img, orientation) => {
      if (orientation === 1) {
        const canvas = this.createCanvas(img.naturalWidth, img.naturalHeight);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');
        ctx.drawImage(img, 0, 0);
        return canvas;
      }

      const { naturalWidth, naturalHeight } = img;
      const [width, height] = this.getOrientedDimensions(naturalWidth, naturalHeight, orientation);

      const canvas = this.createCanvas(width, height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      this.applyOrientationTransform(ctx, width, height, orientation);
      ctx.drawImage(img, 0, 0);

      return canvas;
    }) as Promise<ImageProcessingResult<T>>;
  }

  /**
   * Carica un'immagine e restituisce le sue dimensioni effettive in pixel.
   * Gestisce in modo efficiente le risorse per evitare memory leak.
   *
   * @param source - Immagine sorgente (base64, File o Blob)
   * @returns Promise con width e height in pixel
   *
   * @example
   * const { width, height } = await service.getImageDimensions(imageFile);
   *
   * @throws {Error} Se l'immagine non può essere caricata
   */
  async getImageDimensions(source: ImageSource): Promise<{ width: number; height: number }> {
    let img: HTMLImageElement | null = null;

    try {
      img = await this.loadImage(source);
      return { width: img.naturalWidth, height: img.naturalHeight };
    } finally {
      this.disposeImage(img);
    }
  }

  /**
   * Converte una stringa base64 in un oggetto Blob.
   * Supporta stringhe base64 con o senza il prefisso "data:image/...;base64,".
   *
   * @param base64   - La stringa base64 da convertire
   * @param mimeType - Tipo MIME del Blob (opzionale, estratto dal prefisso se assente)
   * @returns Blob contenente i dati dell'immagine
   *
   * @example
   * const blob = service.base64ToBlob('data:image/jpeg;base64,/9j/4AAQSkZJRg...');
   *
   * @throws {Error} Se la stringa base64 non è valida
   */
  base64ToBlob(base64: string, mimeType?: string): Blob {
    try {
      let dataContentParts: string[] = [];
      let binaryString: string;

      if (base64.startsWith('data:')) {
        dataContentParts = base64.split(',');
        if (dataContentParts.length !== 2) {
          throw new Error('Invalid base64 format');
        }
        if (!mimeType && dataContentParts[0]) {
          const matches = dataContentParts[0].match(/data:([^;]+);/);
          if (matches && matches.length > 1) {
            mimeType = matches[1];
          }
        }
        binaryString = atob(dataContentParts[1]);
      } else {
        binaryString = atob(base64);
        if (!mimeType) {
          mimeType = this.defaultFormat;
        }
      }

      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], { type: mimeType });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to convert base64 to Blob: ${error.message}`, { cause: error });
      } else {
        throw new Error('Failed to convert base64 to Blob');
      }
    }
  }

  /**
   * Calcola le dimensioni target applicando tre vincoli in cascata.
   *
   *  1. "Spingi verso il basso" con il budget MP  → controlla il peso totale
   *  2. "Tira su" con il lato corto minimo        → garantisce leggibilità
   *  3. "Ricappa" con il lato lungo massimo       → limite assoluto di sicurezza
   */
  private calculateSmartSize(
    width: number,
    height: number,
    maxMegapixels: number,
    minShortSide: number,
    maxLongSide: number,
  ): [number, number] {
    let w = width;
    let h = height;

    // ── 1. Budget area ───────────────────────────────────────────────────────
    const maxPixels = maxMegapixels * 1_000_000;
    if (w * h > maxPixels) {
      const areaRatio = Math.sqrt(maxPixels / (w * h));
      w = Math.round(w * areaRatio);
      h = Math.round(h * areaRatio);
    }

    // ── 2. Lato corto minimo ─────────────────────────────────────────────────
    const shortSide = Math.min(w, h);
    if (shortSide < minShortSide) {
      const correction = minShortSide / shortSide;
      w = Math.round(w * correction);
      h = Math.round(h * correction);
    }

    // ── 3. Tetto lato lungo ──────────────────────────────────────────────────
    const longSide = Math.max(w, h);
    if (longSide > maxLongSide) {
      const capRatio = maxLongSide / longSide;
      w = Math.round(w * capRatio);
      h = Math.round(h * capRatio);
    }

    return [w, h];
  }

  /**
   * Sceglie il formato ottimale in base all'aspect ratio.
   * AR > 3.5 → PNG (screenshot, documenti: testo nitido senza artefatti lossy).
   * Tutto il resto → WebP (foto e contenuti misti: peso ridotto a parità di qualità).
   */
  private chooseSmartFormat(width: number, height: number): ImageFormat {
    return this.calcAspectRatio(width, height) > 3.5 ? 'image/png' : 'image/webp';
  }

  private calcAspectRatio(width: number, height: number): number {
    return Math.max(width, height) / Math.min(width, height);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // METODI PRIVATI — infrastruttura canvas/EXIF
  // ══════════════════════════════════════════════════════════════════════════

  private async processImage<T extends ImageSource>(
    image: T,
    format: ImageFormat,
    quality: number,
    processor: (img: HTMLImageElement, orientation: number) => Promise<HTMLCanvasElement>,
  ): Promise<T> {
    let img: HTMLImageElement | null = null;
    let canvas: HTMLCanvasElement | null = null;

    try {
      img = await this.loadImage(image);
      const orientation = await this.getExifOrientation(img);
      canvas = await processor(img, orientation);
      return await this.exportCanvasData(canvas, format, quality, image);
    } finally {
      this.safeDisposeResources(img, canvas);
    }
  }

  private validateInputs(maxSize?: number, format?: ImageFormat, quality?: number): void {
    if (maxSize !== undefined && maxSize <= 0) {
      throw new Error('Invalid max size');
    }
    if (format && !this.supportedFormats.includes(format)) {
      throw new Error(`Unsupported format: ${format}`);
    }
    if (quality !== undefined && (quality < 0 || quality > 1)) {
      throw new Error('Quality must be between 0 and 1');
    }
  }

  private async loadImage(source: ImageSource): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      let objectUrl: string | null = null;

      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };

      img.onload = () => {
        cleanup();
        resolve(img);
      };
      img.onerror = (err) => {
        cleanup();
        reject(new Error('Failed to load image', { cause: err }));
      };

      if (typeof source === 'string') {
        img.src = source;
      } else {
        objectUrl = URL.createObjectURL(source);
        img.src = objectUrl;
      }
    });
  }

  private async getExifOrientation(img: HTMLImageElement): Promise<number> {
    try {
      const exif = await parse(img);
      return exif?.Orientation ?? 1;
    } catch {
      return 1;
    }
  }

  private calculateProportionalSize(
    width: number,
    height: number,
    maxSize: number,
  ): [number, number] {
    const ratio = Math.min(maxSize / width, maxSize / height);
    return [Math.round(width * ratio), Math.round(height * ratio)];
  }

  private createOrientedCanvas(
    width: number,
    height: number,
    orientation: number,
  ): HTMLCanvasElement {
    return this.createCanvas(
      orientation >= 5 && orientation <= 8 ? height : width,
      orientation >= 5 && orientation <= 8 ? width : height,
    );
  }

  private createCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  private applyOrientationTransform(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    orientation: number,
  ): void {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);

    switch (orientation) {
      case 2:
        ctx.transform(-1, 0, 0, 1, width, 0);
        break;
      case 3:
        ctx.transform(-1, 0, 0, -1, width, height);
        break;
      case 4:
        ctx.transform(1, 0, 0, -1, 0, height);
        break;
      case 5:
        ctx.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6:
        ctx.transform(0, 1, -1, 0, height, 0);
        break;
      case 7:
        ctx.transform(0, -1, -1, 0, height, width);
        break;
      case 8:
        ctx.transform(0, -1, 1, 0, 0, width);
        break;
      default:
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
  }

  private async exportCanvasData<T extends ImageSource>(
    canvas: HTMLCanvasElement,
    format: ImageFormat,
    quality: number,
    originalImage: T,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (originalImage instanceof File) {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Conversion failed'));
            resolve(
              new File([blob], originalImage.name, {
                type: format,
                lastModified: originalImage.lastModified,
              }) as T,
            );
          },
          format,
          quality,
        );
      } else if (originalImage instanceof Blob) {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob as T) : reject(new Error('Conversion failed'))),
          format,
          quality,
        );
      } else {
        resolve(canvas.toDataURL(format, quality) as T);
      }
    });
  }

  private getOrientedDimensions(
    width: number,
    height: number,
    orientation: number,
  ): [number, number] {
    return orientation >= 5 && orientation <= 8 ? [height, width] : [width, height];
  }

  private safeDisposeResources(
    img: HTMLImageElement | null,
    canvas: HTMLCanvasElement | null,
  ): void {
    try {
      this.disposeImage(img);
      this.disposeCanvas(canvas);
    } catch (error) {
      console.warn('Error during resource cleanup:', error);
    }
  }

  public disposeImage(img: HTMLImageElement | null): void {
    if (img) {
      img.onload = null;
      img.onerror = null;
      if (img.src.startsWith('blob:')) {
        URL.revokeObjectURL(img.src);
      }
      img.removeAttribute('src');
      if (img.parentNode) {
        img.parentNode.removeChild(img);
      }
    }
  }

  private disposeCanvas(canvas: HTMLCanvasElement | null): void {
    if (canvas) {
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, 1, 1);
      canvas.remove();
    }
  }

  /**
   *
   * @param html
   * @returns base64 form html
   */
  extractBase64FromHtml(html: string): string {
    const base64 = html.replaceAll('"', '').split('src=')[1].replace('>', '');
    return base64;
  }

  calcImportDimensions(
    image: { width: number; height: number },
    maxLongSide: number = 800,
  ): { importWidth: number; importHeight: number } {
    const { width, height } = image;
    const longSide = Math.max(width, height);

    if (longSide <= maxLongSide) {
      return { importWidth: width, importHeight: height };
    }

    const ratio = maxLongSide / longSide;
    return {
      importWidth: Math.round(width * ratio),
      importHeight: Math.round(height * ratio),
    };
  }
}
